import {
  CommonPlatformHeader as DesignSystemPlatformHeader,
  useMediumFromMediaQuery,
} from '@madpaws/design-system';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ACCESS_TOKEN_COOKIE_NAME,
  MD_AUTH_LOCAL_STORAGE_KEY,
  SIGN_IN_ROUTE,
  SIGN_UP_ROUTE,
} from '~/common/constants/auth';
import { transformProfileUrl } from '~/common/utils/sitters';
import { UNREAD_MESSAGES_CHANGED_EVENT } from '~/common/utils/unreadMessages';
import { useAuthenticationContext } from '~/components/AuthenticationProvider/useAuthenticationContext';
import {
  PEAK_PERIOD_PAGE_WEB_EPPO_ID,
  PEAK_PERIOD_PAGE_WEB_EPPO_JSON_PARAMS,
} from '~/components/Eppo/constants';
import { useEppoJSONAssignment, validateEppoJson } from '~/components/Eppo/helpers';
import { resetAnalytics, trackEvent } from '~/components/analytics/analytics';
import { USER_LOGGED_OUT_EVENT_NAME } from '~/components/analytics/constants';
import { SourceType, handleNavigationItemClickedEvent } from '~/components/analytics/utils';
import { isBetaBookingsRoute } from '~/components/bookings/routes';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { validatePeakPeriodData } from '~/components/pages/PeakBookingAvailability/helpers';
import { AuthApi } from '~/services/auth/authApi';
import { UserApi } from '~/services/user/UserApi';
import { removeLocalStorage, TEMP_USER_MOBILE_LOCAL_STORAGE_KEY } from '~/utils/localStorage';

import {
  getLeftNavigationLinks,
  getIconLinks,
  getRightNavigationLinks,
  getPrimaryMenuLinks,
  getSecondaryMenuLinks,
  getSecondaryNavigationLinks,
  getSignUpLoginLink,
} from './utils';

import type { CommonPlatformHeaderUserDetails } from '@madpaws/design-system';
import type { ReactElement } from 'react';
import type { EppoPeakPeriodDataType } from '~/components/Eppo/types';

const { publicRuntimeConfig } = getConfig();

const PlatformHeader = (): ReactElement => {
  const router = useRouter();
  const [unreadMessageCount, setUnreadMessageCount] = useState<number>(0);
  const isMediumFromViewport = useMediumFromMediaQuery();
  const isMediumFromViewportRef = useRef(isMediumFromViewport);
  isMediumFromViewportRef.current = isMediumFromViewport;
  const peakPeriodData = useEppoJSONAssignment(
    PEAK_PERIOD_PAGE_WEB_EPPO_ID
  ) as EppoPeakPeriodDataType;
  const validEppoData = validateEppoJson(peakPeriodData, PEAK_PERIOD_PAGE_WEB_EPPO_JSON_PARAMS);
  const validPeakPeriodData = validatePeakPeriodData(peakPeriodData);
  const showPeakBookingAvailabilityTab = validEppoData && validPeakPeriodData;

  const { isUserLoggedIn, setIsUserLoggedIn, isUserSitter, user } = useAuthenticationContext();

  useEffect(() => {
    if (!isUserLoggedIn) {
      return;
    }

    const refreshUnreadMessageCount = (): void => {
      UserApi.getUserUnreadMessagesCount()
        .then((count) => {
          setUnreadMessageCount(count ?? 0);
        })
        .catch(() => {
          setUnreadMessageCount(0);
        });
    };
    const refreshWhenVisible = (): void => {
      if (document.visibilityState === 'visible') {
        refreshUnreadMessageCount();
      }
    };

    refreshUnreadMessageCount();
    window.addEventListener(UNREAD_MESSAGES_CHANGED_EVENT, refreshUnreadMessageCount);
    window.addEventListener('focus', refreshUnreadMessageCount);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return (): void => {
      window.removeEventListener(UNREAD_MESSAGES_CHANGED_EVENT, refreshUnreadMessageCount);
      window.removeEventListener('focus', refreshUnreadMessageCount);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [isUserLoggedIn]);

  // Built during render rather than in an effect: on wide screens the design
  // system renders nothing without userDetails, so setting it after mount made
  // the avatar vanish for a moment on every page load.
  const userDetails = useMemo((): CommonPlatformHeaderUserDetails | undefined => {
    if (!user) {
      return undefined;
    }

    const { isSitter, avatar, fullName, city, state, rewriteUrl } = user;
    const sitterUrl = transformProfileUrl(city, rewriteUrl, state);

    return {
      avatar: avatar,
      name: fullName,
      sitterUrl: isSitter ? sitterUrl : undefined,
      onClick: (): void => {
        handleNavigationItemClickedEvent(
          sitterUrl,
          'View public profile',
          isMediumFromViewportRef.current
            ? SourceType.HamburgerMenuDesktop
            : SourceType.HamburgerMenuMobile,
          isSitter
        );
      },
    };
  }, [user]);

  const onLogOutClick = (): void => {
    handleNavigationItemClickedEvent(
      '',
      'Log out',
      isMediumFromViewport ? SourceType.HamburgerMenuDesktop : SourceType.HamburgerMenuMobile,
      isUserSitter
    );

    AuthApi.logOut();
    removeLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY);
    removeLocalStorage(TEMP_USER_MOBILE_LOCAL_STORAGE_KEY);
    Cookies.remove(ACCESS_TOKEN_COOKIE_NAME);
    setIsUserLoggedIn(false);
    trackEvent(USER_LOGGED_OUT_EVENT_NAME, { user_id: user?.id });
    resetAnalytics();
    window.location.reload();
  };

  const iconLinks = getIconLinks(isUserLoggedIn ?? false, isUserSitter, unreadMessageCount);
  const leftNavigationLinks = getLeftNavigationLinks(isUserSitter, router.pathname);
  const rightNavigationLinks = getRightNavigationLinks(isUserLoggedIn ?? false, isUserSitter);
  const primaryMenuLinks = getPrimaryMenuLinks(
    isUserLoggedIn ?? false,
    isUserSitter,
    unreadMessageCount,
    isMediumFromViewport ? SourceType.HamburgerMenuDesktop : SourceType.HamburgerMenuMobile,
    showPeakBookingAvailabilityTab
  );
  const secondaryMenuLinks = getSecondaryMenuLinks(
    isUserLoggedIn ?? false,
    isUserSitter,
    isMediumFromViewport ? SourceType.HamburgerMenuDesktop : SourceType.HamburgerMenuMobile
  );
  const secondaryNavigationLinks = getSecondaryNavigationLinks(
    isUserLoggedIn ?? false,
    isUserSitter,
    router.pathname,
    showPeakBookingAvailabilityTab
  );
  const onNavigate = (link: string): void => {
    if (
      isBetaBookingsRoute(router.pathname) &&
      /^\/(account$|bookings(?:\/|$)|booking\/|inbox\/)/.test(link)
    ) {
      window.location.assign(link);
      return;
    }
    void router.push(link);
  };
  const logInLink = getSignUpLoginLink(
    isUserLoggedIn ?? false,
    isUserSitter,
    SIGN_IN_ROUTE,
    'Log in',
    isMediumFromViewport ? SourceType.HeaderPrimary : SourceType.HamburgerMenuMobile,
    onNavigate
  );
  const signUpLink = getSignUpLoginLink(
    isUserLoggedIn ?? false,
    isUserSitter,
    SIGN_UP_ROUTE,
    'Sign up',
    isMediumFromViewport ? SourceType.HeaderPrimary : SourceType.HamburgerMenuMobile,
    onNavigate
  );

  return (
    <DesignSystemPlatformHeader
      domContainerId={DOM_CONTAINER_ID}
      iconLinks={iconLinks}
      leftNavigationLinks={leftNavigationLinks}
      logInLink={logInLink}
      logoFolderPath={`${publicRuntimeConfig.staticPath}/images`}
      onLogOutClick={onLogOutClick}
      primaryMenuLinks={primaryMenuLinks}
      rightNavigationLinks={rightNavigationLinks}
      secondaryMenuLinks={secondaryMenuLinks}
      secondaryNavigationLinks={secondaryNavigationLinks}
      signUpLink={signUpLink}
      userDetails={userDetails}
    />
  );
};

export { PlatformHeader };
